import './assets/styles/main.scss';
import 'react-loading-skeleton/dist/skeleton.css';

import * as Sentry from '@sentry/react';
import * as React from 'react';
import { Route, Routes } from 'react-router';

import RequiredAuth from './auth/RequiredAuth';
import UserAuthenticationState from './auth/UserAuthenticationState';
import DeprecatedNamespaceRedirect from './components/DeprecatedNamespaceRedirect';
import ErrorFallback from './components/ErrorFallback/ErrorFallback';
import LastLeadRedirect from './components/LastLeadRedirect';
import LeadGidRedirect from './components/LeadGidRedirect';
import PersonGidRedirect from './components/PersonGidRedirect';
import RouteLoadingOverlay, {
  RouteLoadingFallback,
  RouteLoadingOverlayProvider
} from './components/RouteLoadingOverlay';
import TwilioFlexUrlRouter from './components/TwilioFlexUrlRouter';
import ExtrasLayoutRoute from './pages/ExtrasLayoutRoute';
import ScoutingReportScriptPreview from './pages/ScoutingReportScripts/ScoutingReportScriptPreview';
import ScoutingSteps from './pages/ScoutingReportScripts/ScoutingSteps';
import authInfo from './services/authInfo';

const AppRoot = React.lazy(() => import('./pages/AppRoot'));
const CoveragesMappings = React.lazy(() => import('./pages/CoveragesMappings'));
const CurrentCustomerInfo = React.lazy(() => import('./pages/CurrentCustomerInfo'));
const CustomerView = React.lazy(() => import('./pages/CustomerView'));
const GuidedSellingExperience = React.lazy(() => import('./pages/GuidedSellingExperience'));
const Lead = React.lazy(() => import('./pages/Lead'));
const RecentLeadsDashboard = React.lazy(() => import('./pages/LeadsDashboard/RecentLeadsDashboard'));
const MetricsDashboards = React.lazy(() => import('./pages/MetricsDashboards'));
const NewCustomer = React.lazy(() => import('./pages/NewCustomer'));
const NewLead = React.lazy(() => import('./pages/NewLead'));
const PeopleDashboard = React.lazy(() => import('./pages/PeopleDashboard'));
const PeopleSearch = React.lazy(() => import('./pages/PeopleSearch'));
const PoliciesDashboard = React.lazy(() => import('./pages/PoliciesDashboard'));
const SalesDashboardTable = React.lazy(() => import('./pages/SalesDashboardTable'));
const ScoutingReportScripts = React.lazy(() => import('./pages/ScoutingReportScripts'));
const TasksDashboard = React.lazy(() => import('./pages/TasksDashboard'));

const App = () => {
  const [isAuthenticatedToBackend, setIsAuthenticatedToBackend] = React.useState(false);

  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <RouteLoadingOverlayProvider>
        <UserAuthenticationState
          isAuthenticatedToBackend={isAuthenticatedToBackend}
          setIsAuthenticatedToBackend={setIsAuthenticatedToBackend}
        />
        <React.Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route path="/crm/*" element={<DeprecatedNamespaceRedirect />} />
            <Route element={<RequiredAuth isAuthenticatedToBackend={isAuthenticatedToBackend} />}>
              <Route element={<ExtrasLayoutRoute />}>
                <Route path="max_client_request" element={<TwilioFlexUrlRouter />} />
                <Route path="leads/gid/:gid" element={<LeadGidRedirect />} />
                <Route path="customers/:customerId/*" element={<PersonGidRedirect />} />
                <Route path="people/:personGid/last_lead" element={<LastLeadRedirect />} />
                <Route element={<AppRoot />}>
                  <Route path="current_customer_info" element={<CurrentCustomerInfo />} />
                  <Route
                    path="scouting_report/*" // TODO: rename path to guided_selling_experience
                    element={<GuidedSellingExperience />}
                  />
                  <Route path="people_search" element={<PeopleSearch />} />
                  <Route path="new_lead" element={<NewLead />} />
                  <Route path="new_customer" element={<NewCustomer />} />
                  <Route path="leads/recent" element={<RecentLeadsDashboard />} />
                  <Route path="leads/:id/*" element={<Lead />} />
                  <Route path="leads" element={<SalesDashboardTable />} />
                  <Route path="policies" element={<PoliciesDashboard />} />
                  <Route path="tasks" element={<TasksDashboard />} />
                  <Route path="people/:gid/*" element={<CustomerView />} />
                  <Route path="customers" element={<PeopleDashboard />} />
                  {authInfo.features.amp_ui_product_tabs && (
                    <>
                      <Route path="dashboards" element={<MetricsDashboards />} />
                      <Route path="coverages_mappings" element={<CoveragesMappings />} />
                    </>
                  )}
                  <Route path="/" element={<SalesDashboardTable />} />
                  <Route path="scouting_steps" element={<ScoutingSteps />} />
                  <Route path="scouting_steps/:stepId" element={<ScoutingSteps />} />
                  <Route path="scouting_report_scripts" element={<ScoutingReportScripts />} />
                  <Route path="scouting_report_scripts/:strategyId" element={<ScoutingReportScripts />} />
                  <Route path="scouting_report_scripts/:configKey/preview" element={<ScoutingReportScriptPreview />} />
                </Route>
              </Route>
            </Route>
          </Routes>
        </React.Suspense>
        <RouteLoadingOverlay />
      </RouteLoadingOverlayProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
